import React, { useState } from 'react';
import { Plus, Minus } from '@phosphor-icons/react';

const FAQS = [
  {
    q: 'Do I need my primary doctor\'s permission to get a second opinion?',
    a: 'No. You do not need permission from your current doctor. We will not contact them or share your records with them unless you explicitly request us to do so. Your consultation with us remains completely confidential.',
  },
  {
    q: 'What if the second opinion contradicts my current doctor\'s diagnosis?',
    a: 'A differing opinion is exactly the value of a second consultation. Our specialist provides their independent professional view based on your records. You can then discuss this opinion with your treating physician, share the report with them, or seek a third opinion. The final treatment decision always rests with you and your treating physician.',
  },
  {
    q: 'How long does it take to receive my opinion?',
    a: 'Basic Consultation (₹1,500): Written opinion within 48 hours of submission. Premium Detailed Consultation (₹2,500): Multi-specialist review with video consultation within 5 business days. Complex cases (rare conditions, multiple subspecialties) may take slightly longer — we will update you proactively if so.',
  },
  {
    q: 'Which medical documents should I upload?',
    a: 'Upload everything relevant: doctor prescriptions, hospital discharge summaries, lab reports (blood tests, urine, etc.), imaging reports (X-Ray, MRI, CT scan, ultrasound, PET scan), pathology/biopsy reports, surgery notes if any, ECG/EEG/EMG reports. We accept PDF, JPG, PNG, and DICOM (.dcm) files. Better documentation = more accurate opinion.',
  },
  {
    q: 'Who reviews my case? What are the doctor\'s qualifications?',
    a: 'Every specialist on our panel is independently verified for MCI/SMC/NMC registration, qualifications, fellowship training, and years of practice (minimum 15 years average experience). You will see the reviewing specialist\'s name, qualifications, and specialty on your final opinion report.',
  },
  {
    q: 'Is my medical data safe and private?',
    a: 'Yes. All data is encrypted in transit (HTTPS/TLS) and at rest. Passwords are bcrypt-hashed. Only the assigned specialist and necessary admin staff can access your records. We are fully compliant with the Digital Personal Data Protection Act, 2023 (DPDP Act, India). We never sell or share your data with third parties for advertising. See our Privacy Policy for complete details.',
  },
  {
    q: 'How does the AI-assisted analysis work?',
    a: 'For each case, our system can generate a preliminary structured analysis using Anthropic\'s Claude Sonnet 4.5 AI model. This draft helps the specialist prepare their opinion efficiently, but every single AI suggestion is reviewed, edited, and verified by a human specialist registered with the NMC or a State Medical Council before being released to you. The final medical opinion is always a human specialist\'s professional judgment.',
  },
  {
    q: 'What if I have a medical emergency?',
    a: 'Do NOT use SecondDoctorOpinion.com for emergencies. If you are experiencing chest pain, breathing difficulty, severe bleeding, stroke symptoms, suicidal thoughts, or any life-threatening condition, immediately call 112 (India emergency services) or visit the nearest hospital emergency department. Our service is for considered second opinions, not urgent care.',
  },
  {
    q: 'Will my opinion be valid for insurance claims or hospital admission?',
    a: 'Our written opinion is a professional medical document signed by the reviewing specialist. While many insurance companies and hospitals accept third-party second opinions, acceptance varies by insurer and institution. We recommend checking with your insurance provider directly. We can provide additional documentation if needed.',
  },
  {
    q: 'How do I pay? Is UPI available?',
    a: 'We accept both card payments via Stripe (Visa, Mastercard, RuPay, Netbanking) and UPI (Google Pay, PhonePe, Paytm) to shivaam.kesarwaani-1@oksbi or +91 88408 04798. For UPI payments, please WhatsApp the payment screenshot with your Case ID to +91 88408 04798 to confirm.',
  },
  {
    q: 'What if my preferred specialty is not in your list?',
    a: 'We currently cover 39 specialties including subspecialties. If your condition requires a very niche subspecialty not currently on our panel, please WhatsApp us at +91 88408 04798. We can either route you to a related specialist who can review your case or refer you to an offline expert. If we cannot find an appropriate specialist within 72 hours, we will issue a full refund automatically.',
  },
  {
    q: 'Can I get a refund if I\'m not satisfied?',
    a: 'Full refund is available before specialist assignment. 50% refund after assignment but before opinion is delivered. No refund after opinion is delivered (the service has been completed). We do not refund based on disagreement with the medical opinion — the value of a second opinion is its independence, not confirming what you hoped to hear. See our Refund Policy for complete details.',
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="faq-section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-2">Frequently Asked</p>
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-primary mb-3">Questions Indian Patients Ask</h2>
          <p className="text-secondary">Clear answers to the most common questions about getting a second medical opinion online.</p>
        </div>

        <div className="space-y-3">
          {FAQS.map((faq, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className="card cursor-pointer"
                onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                data-testid={`faq-item-${idx}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-manrope font-semibold text-primary text-base sm:text-lg pr-2">
                    {faq.q}
                  </h3>
                  <button
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 text-primary"
                    aria-label={isOpen ? 'Collapse answer' : 'Expand answer'}
                  >
                    {isOpen ? <Minus size={18} weight="bold" /> : <Plus size={18} weight="bold" />}
                  </button>
                </div>
                {isOpen && (
                  <p className="text-secondary leading-relaxed mt-3 text-sm sm:text-base" data-testid={`faq-answer-${idx}`}>
                    {faq.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-center mt-8 text-sm text-secondary">
          Have more questions? <a href="https://wa.me/918840804798" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">WhatsApp us at +91 88408 04798</a> or visit our <a href="/contact" className="text-primary font-semibold hover:underline">Contact page</a>.
        </div>
      </div>
    </section>
  );
}
